import React from 'react';
import { cn } from '../../lib/utils.js';
import { ROLE_INFO } from '../../lib/constants.js';
import type { AdminRole, DeviceCommandStatus } from '@hisob/types';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  size?: 'sm' | 'md';
}

export function Badge({ className, variant = 'default', size = 'sm', children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-medium rounded-md border select-none',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs',
        variant === 'default' && 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700',
        variant === 'success' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
        variant === 'warning' && 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
        variant === 'danger' && 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20',
        variant === 'info' && 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/20',
        variant === 'neutral' && 'bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800',
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export function RoleBadge({ role }: { role: AdminRole }) {
  const info = ROLE_INFO[role] || { label: role, badgeColor: 'bg-slate-100 text-slate-700 border-slate-200' };
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border', info.badgeColor)}>
      {info.label}
    </span>
  );
}

export function CommandStatusBadge({ status }: { status: DeviceCommandStatus }) {
  let variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral' = 'neutral';
  let dotColor = 'bg-slate-400';

  switch (status) {
    case 'completed':
      variant = 'success';
      dotColor = 'bg-emerald-500';
      break;
    case 'pending':
      variant = 'warning';
      dotColor = 'bg-amber-500 animate-pulse';
      break;
    case 'acknowledged':
      variant = 'info';
      dotColor = 'bg-indigo-500';
      break;
    case 'failed':
    case 'cancelled':
      variant = 'danger';
      dotColor = 'bg-rose-500';
      break;
    case 'expired':
      variant = 'neutral';
      dotColor = 'bg-slate-400';
      break;
  }

  return (
    <Badge variant={variant}>
      <span className={cn('w-1.5 h-1.5 rounded-full', dotColor)} aria-hidden="true" />
      <span className="capitalize">{status}</span>
    </Badge>
  );
}

export function DeviceStatusBadge({ isOnline, isRevoked }: { isOnline: boolean; isRevoked: boolean }) {
  if (isRevoked) {
    return (
      <Badge variant="danger">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" aria-hidden="true" />
        Revoked
      </Badge>
    );
  }

  if (isOnline) {
    return (
      <Badge variant="success">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
        Online
      </Badge>
    );
  }

  return (
    <Badge variant="neutral">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
      Offline
    </Badge>
  );
}

export function EnvironmentBadge({ env }: { env: string }) {
  const isProd = env.toLowerCase() === 'production';
  const isStaging = env.toLowerCase() === 'staging';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold tracking-wider uppercase rounded border select-none',
        isProd && 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30 animate-pulse',
        isStaging && 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
        !isProd && !isStaging && 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30'
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
      {env}
    </span>
  );
}

export function TelegramStatusBadge({ chatId }: { chatId?: string | null }) {
  if (chatId) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-500/25">
        <svg className="w-3.5 h-3.5 fill-current text-[#229ED9]" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
        </svg>
        <span>Bog'langan</span>
        <span className="font-mono text-[11px] opacity-80">({chatId})</span>
      </span>
    );
  }

  return (
    <Badge variant="neutral">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
      Bog'lanmagan
    </Badge>
  );
}

export function AuthActionBadge({ action }: { action: string }) {
  switch (action) {
    case 'OTP_CODE_SENT':
      return (
        <Badge variant="info">
          <span>📨 OTP Yuborildi</span>
        </Badge>
      );
    case 'LOGIN_SUCCESS':
    case 'OTP_VERIFIED':
      return (
        <Badge variant="success">
          <span>✅ Muvaffaqiyatli Kirish</span>
        </Badge>
      );
    case 'LOGIN_FAILED':
    case 'OTP_VERIFICATION_FAILED':
      return (
        <Badge variant="danger">
          <span>❌ Muvaffaqiyatsiz Urinish</span>
        </Badge>
      );
    case 'TELEGRAM_LINKED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-500/20">
          <span>🔗 Telegram Bog'landi</span>
        </span>
      );
    default:
      return <Badge variant="neutral">{action}</Badge>;
  }
}

